import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CountryShort, CropShort, Direction, HomePageDataResponse, Languages, ShortProduct, UserDetails, ApiError } from "../../../Models";
import { shareableItemTypes } from "../../../Utils/Enums";

interface GeneralSliceState {
    direction: Direction;
    activeLanguage: Languages | string;
    userIsLoggedIn: boolean;
    userNeedToViewSharableItem: boolean;
    userWantsToBeRemembered: boolean;
    userDetails: UserDetails | null;
    appIsReady: boolean;
    crops: CropShort[];
    metricMethods: string[];
    activeMetricMethod: string | undefined;
    countriesWithHaifaProducts: CountryShort[];
    products: ShortProduct[];
    showSaveProcessModal: boolean;
    showClearThisStepModal: boolean;
    userIsNavigateToMainPage: boolean;
    sharedSavedItemId: number;
    changeRecipeErrorFromServer: string | null;
    apiErrors: ApiError;
    typeOfSharableItem: shareableItemTypes;
    isAgri: boolean;
    greenBar: {
        isShow: boolean;
        lastIsShow: boolean;
    };
    currentVisibleSavedItemId: number;
    currentVisibleSavedItemName: string;
    summaryDescription: string;
}

const initialState: GeneralSliceState = {
    direction: Direction.LTR,
    activeLanguage: "en",
    userIsLoggedIn: false,
    userWantsToBeRemembered: false,
    userDetails: null,
    appIsReady: false,
    crops: [],
    metricMethods: [],
    activeMetricMethod: undefined,
    countriesWithHaifaProducts: [],
    products: [],
    showSaveProcessModal: true,
    showClearThisStepModal: true,
    userIsNavigateToMainPage: false,
    userNeedToViewSharableItem: false,
    typeOfSharableItem: 1,
    sharedSavedItemId: 0,
    changeRecipeErrorFromServer: "",
    isAgri: false,
    greenBar: {
        isShow: false,
        lastIsShow: false,
    },
    currentVisibleSavedItemId: 0,
    currentVisibleSavedItemName: "",
    apiErrors: {
        showGeneralErrorModal: false,
        statusCode: 0,
        serviceName: "",
        errorMessage: "",
    },
    summaryDescription: "",
};

export const generalSlice = createSlice({
    name: "general",
    initialState,
    reducers: {
        setCurrentVisibleSavedItemId: (state: GeneralSliceState, action: PayloadAction<number>) => {
            state.currentVisibleSavedItemId = action.payload;
        },
        setSummaryDescription: (state: GeneralSliceState, action: PayloadAction<string>) => {
            state.summaryDescription = action.payload;
        },
        setCurrentVisibleSavedItemName: (state: GeneralSliceState, action: PayloadAction<string>) => {
            state.currentVisibleSavedItemName = action.payload;
        },
        setIsAgri: (state: GeneralSliceState, action: PayloadAction<boolean>) => {
            state.isAgri = action.payload;
        },
        setGreenBar: (state: GeneralSliceState, action: PayloadAction<{ isShow: boolean; lastIsShow: boolean }>) => {
            const { isShow, lastIsShow } = action.payload;
            state.greenBar.isShow = isShow;
            state.greenBar.lastIsShow = lastIsShow;
        },
        setDirection: (state: GeneralSliceState, action: PayloadAction<Direction>) => {
            state.direction = action.payload;
        },
        setApiErrors: (state: GeneralSliceState, action: PayloadAction<ApiError>) => {
            const { showGeneralErrorModal, statusCode, serviceName, errorMessage } = action.payload;
            state.apiErrors.showGeneralErrorModal = showGeneralErrorModal;
            state.apiErrors.statusCode = statusCode;
            state.apiErrors.serviceName = serviceName;
            state.apiErrors.errorMessage = errorMessage;
        },
        setLanguage: (state: GeneralSliceState, action: PayloadAction<Languages | string>) => {
            state.activeLanguage = action.payload;
        },
        setChangeRecipeErrorFromServer: (state: GeneralSliceState, action: PayloadAction<string | null>) => {
            state.changeRecipeErrorFromServer = action.payload;
        },
        setSharedSavedItemId: (state: GeneralSliceState, action: PayloadAction<number>) => {
            state.sharedSavedItemId = action.payload;
        },
        setUser: (
            state: GeneralSliceState,
            action: PayloadAction<{
                user: UserDetails | null;
                rememberMe: boolean;
            }>
        ) => {
            const { user, rememberMe } = action.payload;
            state.userDetails = user;
            state.userWantsToBeRemembered = rememberMe;
            state.userIsLoggedIn = true;
        },
        logOut: (state: GeneralSliceState, action: PayloadAction<boolean>) => {
            state.userDetails = null;
            state.userWantsToBeRemembered = action.payload;
            state.userIsLoggedIn = false;
        },
        setAppIsReady: (state: GeneralSliceState, action: PayloadAction<boolean>) => {
            state.appIsReady = action.payload;
        },
        setUserNeedToViewSharableItem: (state: GeneralSliceState, action: PayloadAction<boolean>) => {
            state.userNeedToViewSharableItem = action.payload;
        },
        setTypeOfSharableItem: (state: GeneralSliceState, action: PayloadAction<shareableItemTypes>) => {
            state.typeOfSharableItem = action.payload;
        },
        setMainPageData: (state: GeneralSliceState, action: PayloadAction<HomePageDataResponse>) => {
            const { crops, metricMethods, countriesWithHaifaProducts, ourProducts } = action.payload;
            state.crops = crops;
            state.metricMethods = metricMethods?.map((m) => m.code) || [];
            state.countriesWithHaifaProducts = countriesWithHaifaProducts;
            state.products = ourProducts;

            // If active metric method is not select or not exist in the array of metric methods, select a new active method
            if (!state.metricMethods.find((m) => m === state.activeMetricMethod)) {
                state.activeMetricMethod = state.metricMethods && state.metricMethods.length > 0 ? state.metricMethods[0] : undefined;
            }
        },
        setActiveMetricMethod: (state: GeneralSliceState, action: PayloadAction<string>) => {
            state.activeMetricMethod = action.payload;
        },
        setShowProcessModal: (state: GeneralSliceState, action: PayloadAction<boolean>) => {
            state.showSaveProcessModal = action.payload;
        },

        setShowClearThisStepModal: (state: GeneralSliceState, action: PayloadAction<boolean>) => {
            state.showClearThisStepModal = action.payload;
        },

        setUserIsNavigateToMainPage: (state: GeneralSliceState, action: PayloadAction<boolean>) => {
            state.userIsNavigateToMainPage = action.payload;
        },
    },
});

export const Actions = generalSlice.actions;
export const Reducer = generalSlice.reducer;
